import type { ColumnSort } from '@/domain';

/*
 * An order the server sorts by, and the column header that shows it. The
 * request of 6 October 2026 (fifth), section 4: the tables on a doctor's page
 * are ordered by the server, a page at a time, so a header there does not sort
 * the rows in hand. It asks for another order, and shows the one in force.
 */

/** A `sort` of the wire as the column that shows it: `-createdAt` is `createdAt`, newest at the top. */
export function columnOfSort(sort: string): ColumnSort {
  return sort.startsWith('-')
    ? { key: sort.slice(1), direction: 'desc' }
    : { key: sort, direction: 'asc' };
}

/** The other way: the header pressed, as the `sort` to ask the server for. */
export function sortOfColumn(column: ColumnSort): string {
  return column.direction === 'desc' ? `-${column.key}` : column.key;
}

/**
 * What a column ordered by the server is "sorted by" on the page: each row's
 * place in the server's answer.
 *
 * The table sorts the rows it is given by the column whose header is lit, and
 * turns the result over when the arrow points down. Handed the value the
 * column shows, it would sort the page a second time by its own idea of that
 * value, which need not be the server's: names by another alphabet, two cases
 * booked in the same minute. Handed this, it leaves the rows exactly as they
 * arrived, whichever way the arrow points.
 */
export function asServed<Row>(
  rows: readonly Row[],
  keyOf: (row: Row) => string,
  descending: boolean,
): (row: Row) => number {
  const place = new Map(rows.map((row, index) => [keyOf(row), index]));
  return (row) => {
    const at = place.get(keyOf(row)) ?? 0;
    return descending ? -at : at;
  };
}
